<script setup>
/**
 * One moment on a Journal page (journal redesign 2026-10-27): the time in the
 * margin, then one ink line, or a keepsake for the bigger moments (a reading,
 * a gift tucked in, a Mystic's message taped in like a photo). Mystics have a
 * SQUARE portrait; seekers a round one. Each line has its own ⋯ (hide, delete).
 */
import { ref, computed } from 'vue'
import JournalGlyph from './JournalGlyph.vue'
import JournalCard from './JournalCard.vue'
import JournalAddOwn from './JournalAddOwn.vue'
import { lineParts } from '@/utils/journal-book'

const props = defineProps({
  m: { type: Object, required: true },
  animate: { type: Boolean, default: false },   // first sight of this page: the gift tucks in
  desktop: { type: Boolean, default: false },
  menuOpen: { type: Boolean, default: false },
  revoking: { type: Boolean, default: false },
  addable: { type: Array, default: () => [] },   // a reading's ✎ menu: the seeker's lines it can still take
  quoteOff: { type: Boolean, default: false },   // its line shows labelled under it instead
  kept: { type: Boolean, default: false },       // on the Kept shelf: its ⋯ holds only "Remove from Kept"
})
const emit = defineEmits(['add-own', 'menu', 'hide', 'delete', 'unkeep', 'open-reading', 'open-lesson', 'open-gift', 'open-sent', 'open-msg', 'open-letter', 'take-back'])

const parts = computed(() => lineParts(props.m))
const confirming = ref(false)
const askTake = ref(false)
const folded = ref(false)

function toggleMenu () { confirming.value = false; emit('menu', props.menuOpen ? '' : props.m.id) }
function closeMenu () { confirming.value = false; emit('menu', '') }

const PALETTE = ['#7b4bb0', '#a0566e', '#3f6f8f', '#5f7a4a', '#8c6628', '#6a4c9c', '#9a4f3c']
function initials (name = '') {
  const w = String(name).trim().split(/\s+/).filter(Boolean)
  return ((w[0] || '?')[0] + (w.length > 1 ? w[w.length - 1][0] : '')).toUpperCase()
}
function tint (name = '') { let h = 0; for (const c of String(name)) h = (h * 31 + c.charCodeAt(0)) >>> 0; return PALETTE[h % PALETTE.length] }

const GLYPH = { altar: 'altar', intent: 'intent', paint: 'paint', talk: 'talk', vote: 'vote', look: 'look', lesson_in: 'lesson', lesson_out: 'lesson', horo: 'horo' }
const glyph = computed(() => GLYPH[props.m.kind] || 'star')
const person = computed(() => props.m.from || props.m.to || props.m.who || null)
const rating = computed(() => Math.max(0, Math.min(5, Number(props.m.rating) || 0)))
const giftOutState = computed(() => {
  const m = props.m
  if (m.revoked_at) return 'taken'
  if (m.opened_at) return 'opened'
  return 'waiting'
})
</script>

<template>
  <div class="mo" :class="{ 'is-hidden': m.hidden, desk: desktop }" :data-kind="m.kind" data-testid="journal-moment">
    <div class="tm">{{ m.time }}</div>
    <div class="ln">
      <!-- A gift received: tucked in with its card. -->
      <template v-if="m.kind === 'gift_in'">
        <div class="who">
          <span v-if="person && person.avatar" class="cp"><img :src="person.avatar" alt="" /></span>
          <span v-else class="cp" :style="{ background: tint(person && person.name) }" aria-hidden="true">{{ initials(person && person.name) }}</span>
          <b>{{ parts[0].text }}</b><JournalGlyph name="gift" class="gm" />
        </div>
        <div class="gk" :class="{ glow: animate }" data-testid="journal-gift-in">
          <span class="tuck" :class="{ tuckin: animate }"><JournalCard :card="m.card" :w="desktop ? 66 : 58" /></span>
          <div class="gbody">
            <div class="gt">{{ m.title || 'A reading made for you' }}</div>
            <div v-if="m.card && m.card.name" class="gs">Your card: <i>{{ m.card.name }}</i></div>
            <div v-if="m.mystic" class="by"><span class="sq s"><img v-if="m.mystic.avatar" :src="m.mystic.avatar" alt="" /><span v-else>{{ initials(m.mystic.name) }}</span></span>Read by {{ m.mystic.name }}</div>
            <div class="act"><button type="button" class="btn-ink" data-testid="journal-gift-open" @click="emit('open-gift', m)">Open</button></div>
          </div>
        </div>
      </template>

      <!-- A gift sent: on the day it was sent; Take it back only while unopened. -->
      <template v-else-if="m.kind === 'gift_out'">
        <div class="who">
          <span v-if="person && person.avatar" class="cp"><img :src="person.avatar" alt="" /></span>
          <span v-else class="cp" :style="{ background: tint(person && person.name) }" aria-hidden="true">{{ initials(person && person.name) }}</span>
          <b>{{ parts[0].text }}</b><JournalGlyph name="gift" class="gm" />
        </div>
        <div class="gk out" data-testid="journal-gift-out">
          <span class="tuck"><JournalCard :card="m.card" :w="44" /></span>
          <div class="gbody">
            <button type="button" class="gt gt-link" :disabled="giftOutState === 'taken'" data-testid="journal-sent-open" @click="emit('open-sent', m)">{{ m.title || `A reading for ${person && person.name}` }}</button>
            <template v-if="giftOutState === 'taken'"><div class="status wait" data-testid="journal-sent-status">You took it back</div></template>
            <template v-else-if="giftOutState === 'opened'">
              <div class="status" data-testid="journal-sent-status">{{ person && person.name }} opened it
                <svg viewBox="0 0 34 24" aria-hidden="true"><path class="ink" pathLength="1" stroke-width="2" d="M3 13.5c2.4 1.6 4 3.4 5.4 6.2C11.6 12.4 18 5.8 31 2.6" /></svg></div>
              <div v-if="m.thanked" class="status">{{ person && person.name }} thanked you</div>
            </template>
            <template v-else>
              <div class="status wait" data-testid="journal-sent-status">Not opened yet</div>
              <template v-if="m.revocable !== false">
                <div v-if="askTake" class="confirm">
                  {{ person && person.name }} will not be able to open it.
                  <div class="row">
                    <button type="button" class="btn-ghost" :disabled="revoking" data-testid="journal-take-back-yes" @click="emit('take-back', m); askTake = false">Yes, take it back</button>
                    <button type="button" class="ilink" @click="askTake = false">Keep it</button>
                  </div>
                </div>
                <button v-else type="button" class="ilink" data-testid="journal-take-back" @click="askTake = true">Take it back</button>
              </template>
            </template>
          </div>
        </div>
      </template>

      <!-- A reading: its question, cards, how it landed, the line that stayed. -->
      <template v-else-if="m.kind === 'reading'">
        <div class="who">
          <span class="sq"><img v-if="m.mystic && m.mystic.avatar" :src="m.mystic.avatar" alt="" /><span v-else>{{ initials(m.mystic && m.mystic.name) }}</span></span>
          <span v-if="m.from_lesson && m.line">{{ m.line }}</span>
          <span v-else>You asked {{ (m.mystic && m.mystic.name) || 'Mystic Aida' }}</span>
        </div>
        <div v-if="m.question" class="q">“{{ m.question }}”</div>
        <div v-if="m.cards.length" class="fan"><JournalCard v-for="(c, i) in m.cards.slice(0, 5)" :key="i" :card="c" :w="46" /></div>
        <div v-if="rating" class="felt">
          <span class="balls" :aria-label="`Rated ${rating} of 5`"><i v-for="n in 5" :key="n" :class="{ f: n <= rating }"></i></span>{{ rating >= 4 ? 'It resonated' : 'Rated' }}
        </div>
        <button v-if="m.from_lesson" type="button" class="jm-from" data-testid="journal-from-lesson" @click="emit('open-lesson', m)">from a lesson about <u>{{ m.from_lesson.about || 'this' }}</u> ›</button>
        <div v-if="m.quote && !quoteOff" class="quote">{{ m.quote }}</div>
        <button type="button" class="ilink" data-testid="journal-reading-open" @click="emit('open-reading', m)">View the reading</button>
        <JournalAddOwn :pieces="addable" @add="emit('add-own', $event)" />
      </template>

      <!-- A Mystic's message, taped in like a photo (a kept picture too: the
           giver's mark is square for a Mystic, round for a seeker). -->
      <template v-else-if="m.kind === 'msg'">
        <div class="who">
          <span v-if="!m.mystic && m.from" class="cp"><img v-if="m.from.avatar" :src="m.from.avatar" alt="" /><span v-else :style="{ background: tint(m.from.name) }" class="cpi">{{ initials(m.from.name) }}</span></span>
          <span v-else class="sq"><img v-if="m.mystic && m.mystic.avatar" :src="m.mystic.avatar" alt="" /><span v-else>{{ initials(m.mystic && m.mystic.name) }}</span></span>
          <b>{{ parts[0].text }}</b>
        </div>
        <button type="button" class="photo" :aria-label="kept ? `Open the day you kept ${m.title || 'it'}` : `Open ${m.mystic && m.mystic.name}’s message: ${m.title || ''}`" data-testid="journal-msg-photo" @click="emit('open-msg', m)">
          <span class="tape l"></span><span class="tape r"></span>
          <img v-if="m.image_url" class="art" :src="m.image_url" alt="" />
          <span v-else class="art art-ph" aria-hidden="true"></span>
          <span class="capt">{{ m.title }}</span>
        </button>
      </template>

      <!-- A letter, sealed. -->
      <template v-else-if="m.kind === 'letter'">
        <div class="who">
          <span class="sq"><img v-if="m.mystic && m.mystic.avatar" :src="m.mystic.avatar" alt="" /><span v-else>{{ initials(m.mystic && m.mystic.name) }}</span></span>
          <b>{{ parts[0].text }}</b>
        </div>
        <div class="env"><span class="e"><span class="seal"></span></span><button type="button" class="ilink" @click="emit('open-letter', m)">Read the letter</button></div>
      </template>

      <!-- The horoscope unfolds in place. -->
      <template v-else-if="m.kind === 'horo'">
        <button type="button" class="unfold" :aria-expanded="folded" @click="folded = !folded">
          <JournalGlyph name="horo" class="gl" /><template v-for="(p, i) in parts" :key="i"><i v-if="p.em">{{ p.text }}</i><template v-else>{{ p.text }}</template></template>
        </button>
        <div v-if="folded && m.text" class="fold">{{ m.text }}</div>
      </template>

      <!-- Someone in your Circle. -->
      <template v-else-if="m.kind === 'circle' || m.kind === 'compat' || m.kind === 'bday'">
        <span class="cp s" :style="{ background: tint(person && person.name) }" aria-hidden="true">{{ initials(person && person.name) }}</span><template v-for="(p, i) in parts" :key="i"><i v-if="p.em">{{ p.text }}</i><template v-else>{{ p.text }}</template></template>
      </template>

      <!-- A Mystic followed, an image of theirs kept. -->
      <template v-else-if="m.kind === 'follow' || m.kind === 'saved'">
        <span class="sq s inl"><img v-if="m.mystic && m.mystic.avatar" :src="m.mystic.avatar" alt="" /><span v-else>{{ initials(m.mystic && m.mystic.name) }}</span></span><template v-for="(p, i) in parts" :key="i"><i v-if="p.em">{{ p.text }}</i><template v-else>{{ p.text }}</template></template>
      </template>

      <!-- Every other small moment: one ink line. -->
      <template v-else>
        <JournalGlyph :name="glyph" class="gl" /><template v-for="(p, i) in parts" :key="i"><i v-if="p.em">{{ p.text }}</i><template v-else>{{ p.text }}</template></template>
      </template>
    </div>

    <span class="more-wrap">
      <button type="button" class="more" :aria-expanded="menuOpen" aria-label="Options for this line" data-testid="journal-line-menu" @click.stop="toggleMenu"><JournalGlyph name="more" /></button>
      <div v-if="menuOpen" class="pop" role="menu" @click.stop>
        <template v-if="confirming">
          <p class="msg">Delete this line for good? It leaves this page; the reading or gift itself is kept where it lives.</p>
          <button type="button" role="menuitem" class="danger" data-testid="journal-line-delete-yes" @click="emit('delete', m); closeMenu()">Delete</button>
          <button type="button" role="menuitem" @click="closeMenu">Keep it</button>
        </template>
        <template v-else-if="kept">
          <button type="button" role="menuitem" data-testid="journal-kept-remove" @click="emit('unkeep', m); closeMenu()">Remove from Kept</button>
        </template>
        <template v-else>
          <button type="button" role="menuitem" data-testid="journal-line-hide" @click="emit('hide', m); closeMenu()">{{ m.hidden ? 'Unhide this line' : 'Hide this line' }}</button>
          <button type="button" role="menuitem" class="danger" data-testid="journal-line-delete" @click="confirming = true">Delete this line…</button>
        </template>
      </div>
    </span>
  </div>
</template>

<style scoped>
.mo { display: grid; grid-template-columns: 40px minmax(0, 1fr) 26px; column-gap: 10px; padding: 10px 0 9px; position: relative; }
.mo.desk { grid-template-columns: 46px minmax(0, 1fr) 28px; column-gap: 14px; }
.is-hidden { opacity: .42; }
.tm { font: italic 600 14.5px/24px var(--j-serif); color: var(--j-hand); text-align: right; white-space: nowrap; }
.ln { font: 500 18px/24px var(--j-serif); color: var(--j-ink); min-width: 0; overflow-wrap: anywhere; }
.ln i { font-style: italic; }
.gl { display: inline-block; width: 17px; height: 17px; vertical-align: -3px; margin-right: 6px; color: var(--j-ink2); }
.unfold { text-align: left; font: inherit; color: inherit; background: none; border: 0; padding: 0; cursor: pointer; }
.fold { margin-top: 8px; padding: 10px 12px; border-radius: 8px; background: rgba(255, 255, 255, .4); box-shadow: inset 0 0 0 1px rgba(42, 24, 69, .1); font: 500 16.5px/1.45 var(--j-serif); color: var(--j-ink); }

.more-wrap { position: relative; }
.more { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--j-ink3); opacity: .55; background: none; border: 0; padding: 0; cursor: pointer; }
.more:hover, .more:focus-visible { opacity: 1; background: rgba(42, 24, 69, .06); }
.more :deep(svg) { width: 16px; height: 16px; }
.pop { position: absolute; z-index: 9; right: 0; top: 30px; min-width: 210px; padding: 6px; border-radius: 10px; background: #fbf6ea; box-shadow: 0 10px 30px rgba(42, 24, 69, .3), 0 0 0 1px rgba(42, 24, 69, .1); }
.pop button { display: block; width: 100%; text-align: left; padding: 9px 10px; border-radius: 7px; font: 500 14px var(--j-ui); color: var(--j-ink); background: none; border: 0; cursor: pointer; }
.pop button:hover { background: rgba(42, 24, 69, .07); }
.pop button.danger { color: #8a2340; }
.pop .msg { margin: 0; padding: 8px 10px; font: 500 13.5px/1.4 var(--j-ui); color: var(--j-ink2); }

.who { display: flex; align-items: center; gap: 8px; font: 600 18px/24px var(--j-serif); color: var(--j-ink); }
.who b { font-weight: 600; }
.sq { width: 28px; height: 28px; border-radius: 6px; overflow: hidden; flex: none; box-shadow: 0 0 0 1px rgba(140, 102, 40, .5); background: #3a1d5c; color: #ffdfa3; display: inline-flex; align-items: center; justify-content: center; font: 700 11px var(--j-ui); }
.sq img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sq.s { width: 20px; height: 20px; border-radius: 4px; font-size: 9px; }
.sq.inl { vertical-align: -4px; margin-right: 6px; }
.cp { width: 28px; height: 28px; border-radius: 50%; flex: none; display: inline-flex; align-items: center; justify-content: center; font: 700 12.5px var(--j-ui); color: #fff4dc; box-shadow: 0 0 0 1.5px rgba(140, 102, 40, .55); overflow: hidden; background: #7b4bb0; }
.cp img { width: 100%; height: 100%; object-fit: cover; }
.cp .cpi { width: 100%; height: 100%; display: inline-flex; align-items: center; justify-content: center; }
.cp.s { width: 20px; height: 20px; font-size: 9.5px; vertical-align: -4px; margin-right: 6px; }
.gm { width: 18px; height: 18px; color: #a77a22; flex: none; }

.gk { position: relative; display: flex; gap: 14px; align-items: flex-start; margin-top: 9px; padding: 12px 12px 12px 10px; border-radius: 10px;
  background: radial-gradient(120% 140% at 0% 0%, rgba(232, 196, 110, .28), rgba(232, 196, 110, .08) 60%, rgba(232, 196, 110, .04)); box-shadow: inset 0 0 0 1px rgba(140, 102, 40, .22); }
.gk.out { background: rgba(42, 24, 69, .04); box-shadow: inset 0 0 0 1px rgba(42, 24, 69, .12); }
.gbody { min-width: 0; }
.tuck { position: relative; flex: none; transform: rotate(-3deg); margin: 2px 0 0 2px; display: inline-flex; }
.tuck::before, .tuck::after { content: ''; position: absolute; width: 16px; height: 16px; z-index: 2; background: linear-gradient(135deg, #e9dcc0 50%, transparent 51%); }
.tuck::before { left: -3px; top: -3px; }
.tuck::after { right: -3px; bottom: -3px; transform: rotate(180deg); }
.gt { font: 600 18px/1.25 var(--j-serif); color: var(--j-ink); }
.gt-link { background: none; border: 0; padding: 0; text-align: left; cursor: pointer; }
.gt-link:disabled { cursor: default; }
.gs { font: 500 15.5px/1.35 var(--j-serif); color: var(--j-ink2); margin-top: 3px; }
.gs i { color: var(--j-ink); font-weight: 600; }
.by { display: flex; align-items: center; gap: 6px; font: 500 14px var(--j-serif); color: var(--j-ink2); margin-top: 6px; }
.act { margin-top: 10px; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.status { display: flex; align-items: center; gap: 6px; font: italic 600 16px var(--j-serif); color: var(--j-hand); margin-top: 5px; }
.status svg { width: 24px; height: 18px; }
.status .ink { fill: none; stroke: currentColor; stroke-linecap: round; }
.status.wait { color: var(--j-ink3); }
.confirm { margin-top: 8px; padding: 10px 12px; border-radius: 8px; background: rgba(255, 255, 255, .45); box-shadow: inset 0 0 0 1px rgba(42, 24, 69, .15); font: 500 15px/1.35 var(--j-serif); color: var(--j-ink); }
.confirm .row { display: flex; gap: 12px; align-items: center; margin-top: 8px; flex-wrap: wrap; }
.tuckin { animation: tuck .9s .35s cubic-bezier(.2, .8, .25, 1) both; }
@keyframes tuck { from { opacity: 0; transform: translateY(-16px) rotate(-9deg); } to { opacity: 1; transform: rotate(-3deg); } }
.glow { animation: glow 2.4s .5s ease-out both; }
@keyframes glow { 0% { box-shadow: inset 0 0 0 1px rgba(140, 102, 40, .22), 0 0 0 0 rgba(232, 196, 110, 0); } 35% { box-shadow: inset 0 0 0 1px rgba(140, 102, 40, .4), 0 0 26px 4px rgba(232, 196, 110, .45); } 100% { box-shadow: inset 0 0 0 1px rgba(140, 102, 40, .22), 0 0 0 0 rgba(232, 196, 110, 0); } }

.q { font: italic 600 21px/1.3 var(--j-serif); color: var(--j-ink); margin-top: 5px; }
.fan { display: flex; margin: 10px 0 2px 2px; }
.fan > * { margin-right: -3px; }
.fan > :nth-child(1) { transform: rotate(-5deg); }
.fan > :nth-child(3) { transform: rotate(5deg) translateY(2px); }
.felt { display: flex; align-items: center; gap: 8px; margin-top: 8px; font: italic 600 16px var(--j-serif); color: var(--j-hand); }
.balls { display: inline-flex; gap: 3px; }
.balls i { width: 9px; height: 9px; border-radius: 50%; border: 1.2px solid var(--j-hand); display: block; box-sizing: border-box; }
.balls i.f { background: var(--j-hand); }
.quote { font: italic 500 16.5px/1.4 var(--j-serif); color: var(--j-ink2); margin-top: 4px; padding-left: 10px; border-left: 2px solid rgba(140, 102, 40, .4); }

.photo { display: block; position: relative; margin: 10px 0 2px 2px; width: min(236px, 100%); padding: 7px 7px 30px; background: #fbf7ee; box-shadow: 0 4px 12px rgba(42, 24, 69, .22); transform: rotate(-1.4deg); text-align: left; border: 0; cursor: pointer; }
.desk .photo { width: 280px; }
.photo .art { display: block; width: 100%; height: auto; aspect-ratio: 16 / 10.5; object-fit: cover; }
.photo .art-ph { background: linear-gradient(180deg, #34436f, #c88a5c 42%, #f7cf83 60%, #b9805a 61%, #3b2a2f); }
.capt { position: absolute; left: 10px; right: 8px; bottom: 6px; font: italic 600 17px var(--j-serif); color: var(--j-ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tape { position: absolute; top: -9px; width: 54px; height: 18px; background: rgba(214, 190, 140, .62); box-shadow: 0 1px 2px rgba(0, 0, 0, .08); z-index: 1; }
.tape.l { left: -12px; transform: rotate(-24deg); }
.tape.r { right: -12px; transform: rotate(22deg); }

.env { display: flex; align-items: center; gap: 12px; margin-top: 9px; }
.env .e { width: 66px; height: 44px; flex: none; position: relative; background: #fbf7ee; box-shadow: 0 3px 8px rgba(42, 24, 69, .2); transform: rotate(-2deg); }
.env .e::before { content: ''; position: absolute; inset: 0; background: linear-gradient(to bottom right, transparent 49.5%, rgba(42, 24, 69, .12) 50%, transparent 51%), linear-gradient(to bottom left, transparent 49.5%, rgba(42, 24, 69, .12) 50%, transparent 51%); }
.env .seal { position: absolute; left: 50%; top: 50%; width: 18px; height: 18px; margin: -9px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #9c4a5e, #5e1f33); box-shadow: 0 1px 2px rgba(0, 0, 0, .3); }

.jm-from { display: block; margin: 8px 0 0; padding: 0; border: 0; background: none; cursor: pointer; text-align: left; font: 500 14px var(--j-ui); color: var(--j-muted, #7a6a8f); }
.jm-from u { color: var(--j-ink); text-underline-offset: 3px; text-decoration-color: rgba(42, 24, 69, .35); }
.jm-from:focus-visible { outline: 2px solid #c9a94e; outline-offset: 2px; border-radius: 4px; }
.ilink { font: 600 13px var(--j-ui); color: var(--j-ink); text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgba(42, 24, 69, .35); margin-top: 6px; display: inline-block; background: none; border: 0; padding: 0; cursor: pointer; }
.btn-ink { white-space: nowrap; display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; border-radius: 999px; background: linear-gradient(180deg, #6d3fa3, #4f2a80); color: #ffe7b5; font: 700 15px var(--j-serif); box-shadow: 0 2px 6px rgba(42, 24, 69, .3); border: 0; cursor: pointer; min-height: 40px; }
.btn-ghost { display: inline-flex; align-items: center; padding: 7px 14px; border-radius: 999px; border: 1.3px solid rgba(42, 24, 69, .45); color: var(--j-ink); font: 600 14.5px var(--j-serif); background: none; cursor: pointer; min-height: 38px; }

</style>
